import type { User } from '../types';
import { hasModuleAccess, hasSubPermission } from '../utils/modulePermissions';

const SESSION_DB_NAME = 'forxa-drawing-offline-session';
const SESSION_DB_VERSION = 1;
const SESSION_STORE = 'session';
const ACTIVE_SESSION_KEY = 'active';
const OFFLINE_SESSION_HOURS = 72;

interface DrawingOfflineSessionRecord {
  key: typeof ACTIVE_SESSION_KEY;
  user: User;
  verifiedAt: number;
  expiresAt: number;
}

function createOfflineUserSnapshot(user: User): User {
  // Keep only identity, presentation, and authorization fields needed to open
  // already-verified drawings. Notification preferences, branding payloads,
  // and unrelated profile data do not belong in the offline access lease.
  return {
    id: user.id,
    email: user.email,
    role: user.role,
    is_developer: user.is_developer,
    is_operator: user.is_operator,
    is_owner_admin: user.is_owner_admin,
    access_scope: user.access_scope,
    module_permissions: user.module_permissions ? { ...user.module_permissions } : undefined,
    sub_module_permissions: user.sub_module_permissions ? { ...user.sub_module_permissions } : undefined,
    name: user.name,
    display_name: user.display_name,
    preferred_language: user.preferred_language,
    timezone: user.timezone,
    date_format: user.date_format,
    theme_preference: user.theme_preference,
  };
}

let databasePromise: Promise<IDBDatabase> | null = null;

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Offline session request failed'));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error || new Error('Offline session transaction was aborted'));
    transaction.onerror = () => reject(transaction.error || new Error('Offline session transaction failed'));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise;
  if (typeof window === 'undefined' || !window.indexedDB) {
    return Promise.reject(new Error('Offline session storage is unavailable'));
  }
  databasePromise = new Promise((resolve, reject) => {
    const request = window.indexedDB.open(SESSION_DB_NAME, SESSION_DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(SESSION_STORE)) {
        request.result.createObjectStore(SESSION_STORE, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => {
        request.result.close();
        databasePromise = null;
      };
      resolve(request.result);
    };
    request.onerror = () => {
      databasePromise = null;
      reject(request.error || new Error('Offline session storage is unavailable'));
    };
  });
  return databasePromise;
}

export function canUseDrawingOffline(user: User | null | undefined): boolean {
  return !user?.permission_preview && hasModuleAccess(user, 'drawings', 'view')
    && hasSubPermission(user, 'drawings.offline_download');
}

export async function saveDrawingOfflineSession(user: User): Promise<boolean> {
  if (!canUseDrawingOffline(user)) {
    await clearDrawingOfflineSession();
    return false;
  }
  const now = Date.now();
  const record: DrawingOfflineSessionRecord = {
    key: ACTIVE_SESSION_KEY,
    user: createOfflineUserSnapshot(user),
    verifiedAt: now,
    expiresAt: now + OFFLINE_SESSION_HOURS * 60 * 60 * 1_000,
  };
  const database = await openDatabase();
  const transaction = database.transaction(SESSION_STORE, 'readwrite');
  transaction.objectStore(SESSION_STORE).put(record);
  await transactionComplete(transaction);
  return true;
}

export async function readDrawingOfflineSession(): Promise<User | null> {
  const database = await openDatabase();
  const transaction = database.transaction(SESSION_STORE, 'readonly');
  const record = await requestResult(
    transaction.objectStore(SESSION_STORE).get(ACTIVE_SESSION_KEY),
  ) as DrawingOfflineSessionRecord | undefined;
  await transactionComplete(transaction);
  if (!record) return null;
  if (record.expiresAt <= Date.now() || !canUseDrawingOffline(record.user)) {
    await clearDrawingOfflineSession();
    return null;
  }
  return record.user;
}

export async function clearDrawingOfflineSession(): Promise<void> {
  if (typeof window === 'undefined' || !window.indexedDB) return;
  const database = await openDatabase().catch(() => null);
  if (database) {
    const transaction = database.transaction(SESSION_STORE, 'readwrite');
    transaction.objectStore(SESSION_STORE).clear();
    await transactionComplete(transaction).catch(() => {});
    database.close();
    databasePromise = null;
  }
  await new Promise<void>((resolve) => {
    const request = window.indexedDB.deleteDatabase(SESSION_DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}
